import { ScaleBand, ScaleLinear } from '@visx/vendor/d3-scale';
import { ResolvedGroupedDataObject } from '../dataUtils';
import { GroupedBar, StackedBar } from './types';

/**
 * Faster version of d3's stack - https://d3js.org/d3-shape/stack
 */
const stackSeries = (data: ResolvedGroupedDataObject[], keys: string[]) => {
    let i;

    const stack = new Map<string, { point: [number, number]; data: ResolvedGroupedDataObject }[]>(
        keys.map((key) => [key, []])
    );

    // Keep track of the last position for each group, so each bar can be stacked correctly
    const groupPositions: Record<string, number> = {};

    // Keep track if we have already seen a group + dataKey, to avoid duplicates
    const seen = new Set<string>();

    for (const d of data) {
        let n = d.groupKeys.length;
        for (i = 0; i < n; ++i) {
            const key = d.groupKeys[i];
            const seenKey = (d.__group__ ?? '') + key;
            if (d[key] !== undefined && !seen.has(seenKey)) {
                let previousPos = groupPositions[d.__group__ ?? ''] ?? 0;
                const value = previousPos + Number(d[key]);
                stack.get(key)?.push({
                    point: [previousPos, value],
                    data: d
                });
                groupPositions[d.__group__ ?? ''] = value;
                seen.add(seenKey);
            }
        }
    }

    return Array.from(stack, ([key, items]) => ({
        key,
        items
    }));
};

/**
 * Generates stacked bars for a bar chart visualization.
 *
 * @param series - The series data to generate bars from.
 * @param dataKeys - The keys in the series data to use for the bars.
 * @param isHorizontal - Whether the bars should be horizontal.
 * @param labelGenerator - Function to generate labels for each bar.
 * @param valueScale - Scale for the bar values.
 * @param categoryScale - Scale for the categories.
 * @param colorScale - Scale for the bar colors.
 * @returns an array of StackedBars
 */
export const generateStackedBars = ({
    series,
    dataKeys,
    isHorizontal,
    labelGenerator,
    valueScale,
    categoryScale,
    colorScale
}: {
    series: ResolvedGroupedDataObject[];
    dataKeys: string[];
    isHorizontal: boolean;
    labelGenerator: (barData: ResolvedGroupedDataObject, key: string) => string;
    valueScale: ScaleLinear<number, number, never>;
    categoryScale: ScaleBand<string>;
    colorScale: (key: string) => string;
}): StackedBar[] => {
    const stacks = stackSeries(series, dataKeys);

    const categorySize = categoryScale.bandwidth();

    return stacks.map((barStack, i) => {
        const { key, items } = barStack;
        return {
            index: i,
            key,
            bars: items.map((bar, j) => {
                const { point } = bar;
                let barSize = 0;
                let barX = 0;
                let barY = 0;
                if (isHorizontal) {
                    barSize = valueScale(point[1]) - valueScale(point[0]);
                    barX = valueScale(point[0]);
                    barY = categoryScale(bar.data.__group__ ?? '') || 0;
                } else {
                    barSize = valueScale(point[0]) - valueScale(point[1]);
                    barY = valueScale(point[1]);
                    barX = categoryScale(bar.data.__group__ ?? '') || 0;
                }

                if (barSize < 0) {
                    barSize = Math.abs(barSize);
                    barX = isHorizontal ? barX - barSize : barX;
                    barY = isHorizontal ? barY : barY - barSize;
                }

                const label = labelGenerator(bar.data, key);

                return {
                    data: bar.data,
                    key,
                    index: j,
                    label,
                    height: isHorizontal ? categorySize : barSize,
                    width: isHorizontal ? barSize : categorySize,
                    x: barX || 0,
                    y: barY || 0,
                    color: colorScale(key)
                };
            })
        };
    });
};

/**
 * Generates grouped bars for a bar chart visualization.
 *
 * @param series - The series data to generate bars from.
 * @param dataKeys - The keys in the series data to use for the bars.
 * @param isHorizontal - Whether the bars should be horizontal.
 * @param labelGenerator - Function to generate labels for each bar.
 * @param height - The height of the chart.
 * @param valueScale - Scale for the bar values.
 * @param categoryScale - Scale for the categories.
 * @param groupScale - Scale for the groups.
 * @param colorScale - Scale for the bar colors.
 * @returns an array of GroupedBars
 */
export const generateGroupedBars = ({
    series,
    dataKeys,
    isHorizontal,
    labelGenerator,
    valueScale,
    categoryScale,
    groupScale,
    colorScale
}: {
    series: ResolvedGroupedDataObject[];
    dataKeys: string[];
    isHorizontal: boolean;
    labelGenerator: (barData: ResolvedGroupedDataObject, key: string) => string;
    valueScale: ScaleLinear<number, number, never>;
    categoryScale: ScaleBand<string>;
    groupScale: ScaleBand<string>;
    colorScale: (key: string) => string;
}): GroupedBar[] => {
    const barSize = groupScale.bandwidth();

    return series.map((group, i) => ({
        index: i,
        x: isHorizontal ? valueScale(0) : categoryScale(group.__group__ ?? '') || 0,
        y: isHorizontal ? categoryScale(group.__group__ ?? '') || 0 : valueScale(0),
        bars: dataKeys
            .filter((key) => group[key] !== undefined)
            .map((key, j) => {
                const value = group[key];
                const label = labelGenerator(group, key);

                let valueSize = valueScale(value) - valueScale(0);

                let barX = isHorizontal ? 0 : groupScale(key) || 0;
                let barY = isHorizontal ? groupScale(key) || 0 : 0;

                if (valueSize < 0) {
                    valueSize = Math.abs(valueSize);
                    barX = isHorizontal ? barX - valueSize : barX;
                    barY = isHorizontal ? barY : barY - valueSize;
                }

                return {
                    data: group,
                    index: j,
                    key,
                    value,
                    height: isHorizontal ? barSize : valueSize,
                    width: isHorizontal ? valueSize : barSize,
                    label,
                    x: barX,
                    y: barY,
                    color: colorScale(key)
                };
            })
    }));
};
